// The sign-in page, and the small full-screen states around it (loading,
// no access). Email link for everyone; Google as an extra for the Thread team.
import React, { useState, useEffect } from "react";
import { sendLink, isEmailLink, completeLink, rememberedEmail, signInWithGoogle } from "./auth";
import { T } from "./theme";

const inputStyle = { width: "100%", padding: "12px 16px", fontSize: 14, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusMd, outline: "none", marginBottom: 12, fontFamily: T.fontSans, background: T.bgSurface, color: T.root };
const primary = { width: "100%", padding: "12px 0", fontSize: 14, fontWeight: 600, color: T.fgOnDark, background: T.root, border: "none", borderRadius: T.radiusMd, cursor: "pointer", fontFamily: T.fontSans };
const quiet = { fontSize: 12, fontWeight: 600, color: T.fgMuted, background: "none", border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusMd, padding: "8px 16px", cursor: "pointer", fontFamily: T.fontSans };

export function Card({ title, children }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bgInverse, fontFamily: T.fontSans }}>
      <div className="login-card" style={{ background: T.canvas, borderRadius: T.radiusLg, padding: 40, width: 380, textAlign: "center", boxShadow: T.shadowLg }}>
        <div style={{ color: T.canopy, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>Thread Hub</div>
        {title && <h2 style={{ fontSize: 22, fontWeight: 700, color: T.root, marginBottom: 12 }}>{title}</h2>}
        {children}
      </div>
    </div>
  );
}

const Note = ({ children, tone }) => <p style={{ color: tone === "error" ? T.statusOff : T.fgMuted, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>{children}</p>;

export function Loading() {
  return (<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bgSurface, fontFamily: T.fontSans }}><p style={{ color: T.fgMuted, fontSize: 14 }}>Loading...</p></div>);
}

export function NoAccess({ email, onSignOut }) {
  return (
    <Card title="No access yet">
      <Note>{email ? <>You're signed in as <strong>{email}</strong>, but this address doesn't have access to a Thread Hub page.</> : "This address doesn't have access to a Thread Hub page."}</Note>
      <Note>If you think it should, reply to your Thread contact and we'll set it up.</Note>
      <button onClick={onSignOut} style={quiet}>Use a different email</button>
    </Card>
  );
}

export default function SignIn() {
  const linkInUrl = isEmailLink();
  const [email, setEmail] = useState(linkInUrl ? rememberedEmail() : "");
  const [state, setState] = useState(linkInUrl ? "completing" : "enter"); // enter | sending | sent | completing | confirm
  const [error, setError] = useState("");

  // Arrived from the email: finish sign-in. If this browser didn't send the
  // link (another device), ask for the address first.
  useEffect(() => {
    if (!linkInUrl) return;
    if (!email) { setState("confirm"); return; }
    finish(email);
  }, []);

  const finish = async (addr) => {
    setState("completing"); setError("");
    try { await completeLink(addr.trim()); }
    catch (e) { setError("That link has expired or was already used. Enter your email for a new one."); setState("enter"); window.history.replaceState(null, "", window.location.pathname); }
  };

  const send = async () => {
    const addr = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) { setError("Enter a valid email address."); return; }
    setState("sending"); setError("");
    try { await sendLink(addr); setState("sent"); }
    catch (e) { setError("We couldn't send a link just now. Try again in a few minutes."); setState("enter"); }
  };

  const google = async () => {
    setError("");
    try { await signInWithGoogle(); } catch (e) { if (e && e.code !== "auth/popup-closed-by-user") setError("Google sign-in didn't complete."); }
  };

  if (state === "completing") return <Card title="Signing you in"><Note>One moment...</Note></Card>;

  if (state === "sent") return (
    <Card title="Check your email">
      <Note>If <strong>{email.trim()}</strong> has access, a sign-in link is on its way. Open it on this device.</Note>
      <button onClick={() => setState("enter")} style={quiet}>Use a different email</button>
    </Card>
  );

  const confirming = state === "confirm";
  const submit = () => (confirming ? finish(email) : send());
  return (
    <Card title="Sign in">
      <Note>{confirming ? "Confirm the email address the link was sent to." : "Enter your email and we'll send you a sign-in link. No password needed."}</Note>
      <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} onKeyDown={e => e.key === "Enter" && submit()}
        placeholder="you@company.com" autoComplete="email" aria-label="Email address" style={{ ...inputStyle, border: `1px solid ${error ? T.statusOff : T.borderSubtle}` }} />
      {error && <Note tone="error">{error}</Note>}
      <button onClick={submit} disabled={state === "sending"} style={{ ...primary, marginBottom: 24, opacity: state === "sending" ? 0.7 : 1 }}>
        {confirming ? "Sign in" : state === "sending" ? "Sending..." : "Email me a link"}
      </button>
      {!confirming && (
        <div style={{ borderTop: `1px solid ${T.borderSubtle}`, paddingTop: 20 }}>
          <button onClick={google} style={quiet}>Thread team: sign in with Google</button>
        </div>
      )}
    </Card>
  );
}
